import type { CustomTranslations } from '@checkout.com/checkout-web-components';
import type { Appearance } from '@stripe/stripe-js';

export const getStripeAppearance = (isDarkMode?: boolean): Appearance => {
    return {
        theme: isDarkMode ? 'night' : 'stripe',
        variables: {
            colorPrimary: '#0AC285',
            colorBackground: isDarkMode ? '#1a1a1a' : '#ffffff',
            colorText: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
            fontFamily:
                "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif",
            fontSizeBase: '16px',
            borderRadius: '8px',
            spacingUnit: '4px',
        },
        rules: {
            '.Input': {
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)',
                backgroundColor: 'transparent',
                padding: '12px',
                fontSize: '16px',
                lineHeight: '20px',
            },
            '.Input:focus': {
                borderColor: '#0AC285',
                outline: 'none',
                boxShadow: 'none',
            },
            '.Label': {
                fontSize: '14px',
                fontWeight: '500',
                lineHeight: '20px',
                marginBottom: '4px',
            },
        },
    };
};

export const getCheckoutTemplate = (isDarkMode?: boolean) => {
    // Checkout renders card fields in hosted iframes, where a transparent form
    // background exposes the iframe's white canvas instead of the parent modal.
    const colorFormBackground = isDarkMode ? 'rgba(19, 22, 26, 1)' : 'rgba(255, 255, 255, 1)';

    return {
        colorBackground: 'transparent',
        colorBorder: 'transparent',
        colorPrimary: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
        colorSecondary: '#828687',
        colorAction: isDarkMode ? '#0AC285' : '#0AC285',
        colorOutline: isDarkMode ? 'rgba(51, 51, 51, 1)' : 'rgba(255, 255, 255, 1)',
        colorSuccess: isDarkMode ? '#0AC285' : '#0AC285',
        colorError: isDarkMode ? 'rgba(255, 77, 106, 1)' : 'rgba(217, 22, 103, 1)',
        colorDisabled: '#AAAAAA',
        colorInverse: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        colorFormBackground,
        colorFormBorder: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        borderRadius: ['8px', '8px'],
        subheading: {
            fontFamily:
                "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 500,
            letterSpacing: 0,
        },
        footnote: {
            fontFamily:
                "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 400,
            letterSpacing: 0,
        },
        button: {
            fontFamily:
                "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
            fontSize: '16px',
            color: isDarkMode ? '#000000' : '#FFFFFF',
            lineHeight: '24px',
            fontWeight: 500,
            letterSpacing: 0,
        },
        input: {
            fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
            fontSize: '16px',
            lineHeight: '20px',
            fontWeight: 400,
            letterSpacing: 0,
            color: isDarkMode ? '#FFFFFF' : '#000000',
        },
        label: {
            fontFamily:
                "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 500,
            letterSpacing: 0,
        },
    } as any;
};

export const getCheckoutTranslations = (operation: 'deposit' | 'withdrawal', amount: string, fee?: string) => {
    const depositText = `Deposit ${amount}`;
    const withdrawalText = `Withdraw ${amount}${!!fee ? ` + ${fee} fee` : ''}`;

    switch (operation) {
        case 'deposit':
            return {
                'en-US': {
                    'pay_button.pay': depositText,
                    'form.full_name.placeholder': '',
                },
            } as CustomTranslations;
        case 'withdrawal':
            return {
                'en-US': {
                    'pay_button.pay': withdrawalText,
                    'pay_button.payment_processing': 'Processing',
                    'form.full_name.placeholder': '',
                },
            } as CustomTranslations;
    }
};

export type PaymentStatus = 'pending' | 'success' | 'failure';
export interface DepositLimitsByRail {
    ach: string;
    wire: string;
    crypto: string;
    debit: string;
}
export interface DepositLimitsByRailReturn {
    limits: DepositLimitsByRail;
}

export interface WithdrawalLimitItem {
    amountInDollars: number;
    daysLeft: number;
}

export interface WithdrawalLimits {
    card: WithdrawalLimitItem[];
    savedCards: Record<string, WithdrawalLimitItem[]>;
    bankAccounts: Record<string, WithdrawalLimitItem[]>;
    plaidBankAccounts: Record<string, WithdrawalLimitItem[]>;
    crypto: WithdrawalLimitItem[];
    stripe: WithdrawalLimitItem[];
    paypal: WithdrawalLimitItem[];
    venmo: WithdrawalLimitItem[];
}

export interface DebitCard {
    id: string;
    expiry_month: string;
    expiry_year: string;
    mask: string;
    name: string;
    scheme: string;
    type: string;
    vendor: string;
}

export interface CashAppAccount {
    cashtag?: string;
    payment_method_id: string;
}

/**
 * Wraps a payment failure for the support snackbar.
 *
 * The message comes from Stripe or from the exchange, both of which write whole
 * sentences ending in a full stop, so the suffix must not add another — that is
 * what produced "try a different card.. Tap to contact support".
 */
export const paymentErrorSnackbarText = (message?: string) => {
    const sentence = (message || 'Something unexpected happened').trim().replace(/[.!?]+$/, '');
    return `Payment error: ${sentence}. Tap to contact support`;
};
